<template>
  <!-- 상단 툴바처럼 자연스럽게 녹이기 -->
  <div class="room-toolbar">
    <div class="room-actions">
      <input v-model="roomName" placeholder="방 이름" class="room-input" />
      <button @click="joinRoom" class="room-button">입장</button>
      <button @click="leaveRoom" class="room-button leave">나가기</button>
    </div>

    <div class="room-status" v-if="connected">
      <span class="room-status-label">현재 방:</span>
      <span class="room-name">{{ roomName }}</span>
    </div>
  </div>

  <div v-if="connected" class="app-wrapper" >
    <!-- 왼쪽 툴바 -->
    <div class="toolbar">
      <h3>도구</h3>
      <label for="colorPicker">색상 선택</label>
      <input style="width: 100%;" id="colorPicker" type="color" v-model="selectedColor" />

      <label for="brushSize">크기 선택</label>
      <input id="brushSize" type="range" min="1" max="20" v-model="brushSize" />

      <button @click="toggleEraser" :class="{ active: isErasing }">
        {{ isErasing ? '펜' : '지우개' }}
      </button>
      <button @click="clearCanvas">초기화</button>
    </div>

    <!-- 캔버스 -->
    <canvas ref="canvas" @mousedown="startDrawing" @mousemove="draw" @mouseup="stopDrawing"></canvas>

    <!-- 채팅 패널 -->
    <ChatPanel :stompClient="stompClient" :roomName="roomName" />
  </div>
</template>
  
<script setup>
import '@/styles/drawingBoard.css';
import { onMounted, ref, shallowRef, watch, nextTick } from 'vue';
import { connectWebSocket, disconnectWebSocket, subscribeDrawRoom, unsubscribeDrawRoom, sendDrawMessage } from '@/utils/socketService';
import ChatPanel from '@/components/ChatPanel.vue';

const canvas = ref(null);
const selectedColor = ref('#000000');
const brushSize = ref(2);
const isErasing = ref(false);
const connected = ref(false);
const roomName = ref("");

let ctx = null;
let drawing = false;
const stompClient = shallowRef(null);
let lastX = 0;
let lastY = 0;

onMounted(() => {
  roomName.value = '';
}) 

watch(connected, async (visible) => {
  if (visible) {
    await nextTick();
    const cvs = canvas.value;
    ctx = cvs.getContext('2d');

    cvs.style.width = '100%';
    cvs.style.height = '70vh';

    const scale = window.devicePixelRatio || 1;
    cvs.width = cvs.offsetWidth * scale;
    cvs.height = cvs.offsetHeight * scale;
    ctx.scale(scale, scale);

    cvs.addEventListener('mouseup', () => {
      drawing = false;
      updateCursor();
    });

    cvs.addEventListener('mouseleave', () => {
      drawing = false;
      updateCursor();
    });

    cvs.addEventListener('mousedown', (e) => {
      drawing = true;
      const { x, y } = getCanvasCoordinates(e);
      lastX = x, lastY = y;
      updateCursor();
      drawAndSend(x, y);
    });

    cvs.addEventListener('mousemove', (e) => {
      if (!drawing) return;
      const { x, y } = getCanvasCoordinates(e);
      drawAndSend(x, y);
    });

    cvs.addEventListener('touchend', (e) => {
      e.preventDefault();
      drawing = false;
    }, { passive: false });

    cvs.addEventListener('touchstart', (e) => {
      e.preventDefault();
      drawing = true;
      const { x, y } = getCanvasCoordinates(e);
      lastX = x, lastY = y;
    }, { passive: false });

    cvs.addEventListener('touchmove', (e) => {
      e.preventDefault();
      if (!drawing) return;
      const { x, y } = getCanvasCoordinates(e);
      drawAndSend(x, y);
    }, { passive: false });

  }
});

const drawAndSend = (newX, newY) => {
  const color = isErasing.value ? '#ffffff' : selectedColor.value;

  const drawData = {
    type: 'draw',
    fromX: lastX,
    fromY: lastY,
    toX: newX,
    toY: newY,
    color: color,
    size: brushSize.value,
  };

  drawLine(drawData);

  if (stompClient.value?.connected) {
    sendDrawMessage(stompClient.value, roomName.value, drawData);
  }

  lastX = newX;
  lastY = newY;
};

function getCanvasCoordinates(e) {
  const rect = canvas.value.getBoundingClientRect();
  let clientX, clientY;

  if (e.touches && e.touches.length > 0) {
    clientX = e.touches[0].clientX;
    clientY = e.touches[0].clientY;
  } else {
    clientX = e.clientX;
    clientY = e.clientY;
  }

  return {
    x: clientX - rect.left,
    y: clientY - rect.top
  };
}

const drawLine = ({ fromX, fromY, toX, toY, color, size }) => {
  if (!ctx) return;
  ctx.strokeStyle = color || '#000000';
  ctx.lineWidth = size || 2;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(fromX, fromY);
  ctx.lineTo(toX, toY);
  ctx.stroke();
};

const updateCursor = () => {
  if (!canvas.value) return;
  canvas.value.style.cursor = isErasing.value ? 'cell' : 'crosshair';
};

const toggleEraser = () => {
  isErasing.value = !isErasing.value;
};

const clearCanvas = () => {
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.value.width, canvas.value.height);

  const clearData = { type: 'clear' };
  if (stompClient.value?.connected) {
    sendDrawMessage(stompClient.value, roomName.value, clearData);
  }
};

const clearCanvasRemote = () => {
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.value.width, canvas.value.height);
};

const joinRoom = async () => {
  if (!roomName.value.trim()) {
    alert("방 이름을 입력하세요!");
    return;
  }

  stompClient.value = await connectWebSocket();

  subscribeDrawRoom(stompClient.value, roomName.value, (data) => {
    if (data.type === 'clear') {
      clearCanvasRemote();
    } else if (data.type === 'draw') {
      drawLine(data);
    }
  });

  connected.value = true;
};

const leaveRoom = () => {
  if (roomName.value.trim()) {
    unsubscribeDrawRoom(stompClient.value, roomName.value);
    stompClient.value = disconnectWebSocket(stompClient.value);
    connected.value = false;
    roomName.value = '';
  }
};

</script>
